/**
 * Encrypts batches to the ingest public key, so the network tab and extensions watching
 * requests see ciphertext, not our signals. It doesn't stop anyone reading this code or
 * posting fake batches (that needs a server-issued session token).
 *
 * Each page load makes an ephemeral P-256 key pair; ECDH with the ingest key, then
 * HKDF-SHA-256 (salt: the ephemeral public key), gives an AES-256-GCM key used for every
 * batch from that page with a fresh 12-byte IV. Ingest repeats the ECDH in KMS, where
 * the private key never leaves. ingest/src/envelope.ts opens it.
 */

export interface Envelope {
  /** Envelope format version. */
  ac: 1;
  /** Which ingest key the batch was sealed to, for rotation. */
  kid: string;
  /** Ephemeral public key, raw uncompressed point, base64. */
  epk: string;
  iv: string;
  /** Ciphertext with the GCM tag appended, base64. */
  ct: string;
}

export const HKDF_INFO = 'agentcatch batch v1';

export type Sealer = (plaintext: string) => Promise<string>;

const b64 = (buf: ArrayBuffer | Uint8Array): string => {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
};

const unb64 = (s: string): Uint8Array<ArrayBuffer> => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/**
 * Returns a function that seals a batch, or null when this browser has no WebCrypto
 * (non-secure pages). Callers then drop batches rather than send them in the clear.
 */
export const createSealer = (publicKey: string, kid: string): Sealer | null => {
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  if (!subtle) return null;

  let setup: Promise<{ key: CryptoKey; epk: string }> | undefined;
  const pageKey = () => {
    setup ??= (async () => {
      const ingestKey = await subtle.importKey('spki', unb64(publicKey), { name: 'ECDH', namedCurve: 'P-256' }, false, []);
      const pair = await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']);
      const epkRaw = new Uint8Array(await subtle.exportKey('raw', pair.publicKey));
      const shared = await subtle.deriveBits({ name: 'ECDH', public: ingestKey }, pair.privateKey, 256);
      const hkdf = await subtle.importKey('raw', shared, 'HKDF', false, ['deriveKey']);
      const key = await subtle.deriveKey(
        { name: 'HKDF', hash: 'SHA-256', salt: epkRaw, info: new TextEncoder().encode(HKDF_INFO) },
        hkdf, { name: 'AES-GCM', length: 256 }, false, ['encrypt'],
      );
      return { key, epk: b64(epkRaw) };
    })();
    return setup;
  };

  return async (plaintext) => {
    const { key, epk } = await pageKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(plaintext));
    const env: Envelope = { ac: 1, kid, epk, iv: b64(iv), ct: b64(ct) };
    return JSON.stringify(env);
  };
};
